// src/app/core/services/navigation-service.ts
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FormDataService, ProductData } from './form-data-service';
import { SummaryService } from './summary-service';
import { PayloadService } from './payload-service';
import { SignupAPIService } from './signup-apiservice';
import { SignupRedirectService } from './signup-redirect-service';
import { LoadingService } from './loading-service';
import { Flow, getCleanPath, getFlowFromUrl } from '../utils/flow-utils';
import { TranslationService } from './translation-service'; // 👈 Added TranslationService
import { TextService } from './text-service';
import { AwsLogger } from './aws-logger';
import { ErrorService } from './error-service';
// import { GTMService } from './gtm-service';


@Injectable({ providedIn: 'root' })
export class NavigationService {
  errorMessage: string | null = null;
  constructor(
    private router: Router,
    private formData: FormDataService,
    private summaryService: SummaryService,
    private payloadService: PayloadService,
    private signupApi: SignupAPIService,
    private signupRedirect: SignupRedirectService,
    private loadingService: LoadingService,
    private translationService: TranslationService,
    private textService: TextService,
    private loggerService: AwsLogger,
    private errorService: ErrorService,
  ) { }

  public getFlow(): Flow {
    return getFlowFromUrl(this.router.url);
  }

  async goNext() {
    const current = getCleanPath(this.router);
    const lang = this.translationService.getLanguage();

    // Detect flow and subflow (e.g. "cloud/sddc-flex" or "apps/reseller")
    const match = current.match(new RegExp(`/${lang}/([^/]+)(?:/([^/]+))?`));
    const flow = match ? match[1] : '';
    const subflow = match && match[2] ? match[2] : '';

    // =======================================================
    // STEP: PRODUCTS  (not used by cloud)
    // =======================================================
    if (current.endsWith('/products') && flow !== 'cloud') {
      const summary = await firstValueFrom(this.summaryService.summary$);

      const hasInvalid =
        summary.items.some((item) =>
          this.summaryService.hasInvalidQuantity(item.cartLineId ?? item.id)
        ) || this.summaryService.hasAnyInvalidAddonQuantity();
      if (hasInvalid) {
        if (flow === 'office365') {
          this.errorMessage = this.textService.getText('o365_max');
        }
        console.warn('Quantity exceeds limit');
        return;
      }

      if (flow === 'office365' && this.summaryService.hasO365TotalLicenseLimitExceeded()) {
        this.errorMessage = this.textService.getText('o365_max');
        return;
      }

      if (flow === 'office365' && this.summaryService.hasAnyO365CartLineAddonQuantityExceeded()) {
        this.errorMessage = this.textService.getText('o365_addon_quantity_exceeds_parent');
        return;
      }

      let hasProducts = false;
      if (flow === 'apps') {
        hasProducts = summary.quantity > 0 && summary.totalMonthly > 0;
      } else if (flow === 'office365') {
        hasProducts = this.summaryService.getO365CartLineCount() > 0;
      } else if (flow === 'reseller') {
        hasProducts = true;
      }

      if (!hasProducts) {
        //alert('Please select at least one product.');
        this.errorMessage = 'Please select at least one product.';
        setTimeout(() => {
          const errorElement = document.getElementById('errorBox');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }
      this.errorMessage = null;


      const productData: ProductData[] = summary.items.map(i => ({
        id: i.id,
        name: i.name,
        quantity: i.quantity,
        price: i.unitPrice,
      }));
      this.formData.setProductsData(productData);

      if (flow === 'apps' && subflow === 'reseller') {
        this.router.navigate([`/${lang}/apps/reseller/account`]);
      } else {
        this.router.navigate([`/${lang}/${flow}/account`]);
      }
      return;
    }

    // =======================================================
    // STEP: ACCOUNT
    // =======================================================
    if (current.endsWith('/account')) {
      let valid = false;

      if (flow === 'cloud') {
        const cloudAccount = this.formData.getCloudAccountData();
        valid = !!cloudAccount && !!cloudAccount.verified;
      } else {
        const account = this.formData.getAccountData();
        valid = !!account;
      }

      if (!valid) {
        alert('Please complete the account form.');
        return;
      }

      if (flow === 'apps' && subflow === 'reseller') {
        this.router.navigate([`/${lang}/apps/reseller/address`]);
      } else if (flow === 'cloud') {
        this.router.navigate([`/${lang}/cloud/${subflow}/address`]);
      } else {
        this.router.navigate([`/${lang}/${flow}/address`]);
      }
      return;
    }

    // =======================================================
    // STEP: ADDRESS
    // =======================================================
    if (current.endsWith('/address')) {
      let valid = false;

      if (flow === 'cloud') {
        const cloudAddress = this.formData.getCloudAddressData();
        valid = !!cloudAddress;
      } else {
        const address = this.formData.getAddressData();
        valid = !!address;
      }

      if (!valid) {
        alert('Please complete the address form.');
        return;
      }

      if (flow === 'office365' && this.summaryService.hasO365TotalLicenseLimitExceeded()) {
        this.errorMessage = this.textService.getText('o365_max');
        return;
      }

      if (flow === 'office365' && this.summaryService.hasAnyO365CartLineAddonQuantityExceeded()) {
        this.errorMessage = this.textService.getText('o365_addon_quantity_exceeds_parent');
        return;
      }


      let token = this.formData.getCaptchaToken();

      if (!token) {
        this.formData.triggerCaptchaExecute();

        token = await new Promise<string | null>(resolve => {
          const sub = this.formData.captchaToken$.subscribe(t => {
            if (t) {
              sub.unsubscribe();
              resolve(t);
            }
          });
        });
      }

      this.loadingService.show();
      const finalPayload = await firstValueFrom(this.payloadService.payload$);

      //Trigger error
      // if (finalPayload?.contacts?.contact?.[0]?.user) {
      //   finalPayload.contacts.contact[0].user.username = 'test';
      // }

      const isResellerFlow = flow === 'reseller' || (flow === 'apps' && subflow === 'reseller');
      const shouldSubmit =
        flow === 'cloud' ||
        isResellerFlow ||
        (finalPayload?.order?.item && finalPayload.order.item.length > 0);

      if (shouldSubmit) {
        try {
          this.errorService.clear();
          sessionStorage.setItem('signupPayload', JSON.stringify(finalPayload));
          sessionStorage.setItem('signupFlow', flow);

          const response = await firstValueFrom(this.signupApi.createSignup(finalPayload));
          console.log('response', response);

          if (response?.sessionInfo) {
            sessionStorage.setItem('pfwaSessionInfo', response.sessionInfo);

            this.loggerService
              .log(finalPayload, 'No error', true, flow)
              .subscribe({
                next: r => {
                  console.log('LOGGER RESPONSE', r);
                },
                error: e => {
                  console.log('LOGGER ERROR', e);
                },
                complete: () => {
                  console.log('LOGGER COMPLETE');
                }
              });
          }

          this.signupRedirect.redirectToPfwa(response.pfwaSessionId, response.sessionInfo);

        } catch (err) {
          const httpErr = err as any;

          const detailedError =
            httpErr?.error?.message ||
            httpErr?.error?.error?.message ||
            httpErr?.message ||
            JSON.stringify(httpErr);

          this.errorService.show(detailedError);
          this.loggerService.log(finalPayload, detailedError, false, flow).subscribe();

          this.formData.resetCaptcha();
          this.loadingService.hide();

          this.loadingService.hide();

        } finally {
          // optional hide here
        }
      } else {
        this.loadingService.hide();
        this.errorService.show('Incomplete payload');
        this.loggerService.log(finalPayload, 'Incomplete payload', false, flow).subscribe();
      }


    }

  }

  goBack() {
    const current = this.router.url;
    const lang = this.translationService.getLanguage();

    // Detect flow and subflow (e.g. "cloud/sddc-flex" or "apps/reseller")
    const match = current.match(new RegExp(`/${lang}/([^/]+)(?:/([^/]+))?`));
    const flow = match ? match[1] : '';
    const subflow = match && match[2] ? match[2] : '';

    // ===== CLOUD =====
    if (flow === 'cloud') {
      if (current.endsWith('/account')) {
        // stay on same page for both sddc-flex or openstack-flex
        return;
      } else if (current.endsWith('/address')) {
        // go back to /account for same subflow
        this.router.navigate([`/${lang}/cloud/${subflow}/account`]);
      } else if (current.endsWith('/confirmation')) {
        // go back to /address for same subflow
        this.router.navigate([`/${lang}/cloud/${subflow}/address`]);
      }
      return;
    }

    // ===== OTHER FLOWS =====
    if (current.endsWith('/account')) {
      if (flow === 'apps' && subflow === 'reseller') return;
      this.router.navigate([`/${lang}/${flow}/products`]);
    }
    else if (current.endsWith('/address')) {
      if (flow === 'apps' && subflow === 'reseller') {
        this.router.navigate([`/${lang}/apps/reseller/account`]);
      } else {
        this.router.navigate([`/${lang}/${flow}/account`]);
      }
    }
    else if (current.endsWith('/confirmation')) {
      this.router.navigate([`/${lang}/${flow}/address`]);
    }
  }

  getCurrentStep(): number {
    const current = this.router.url;
    const flow = this.getFlow();

    if (current.endsWith('/products')) return 1;

    if (current.endsWith('/account')) {
      return flow === 'reseller' ? 1 : 2;
    }

    if (current.endsWith('/address')) {
      return flow === 'reseller' ? 2 : 3;
    }

    if (current.endsWith('/confirmation')) {
      return flow === 'reseller' ? 3 : 4;
    }

    return 0;
  }
}